import LoadingIcon from '@/components/Loading';
import ENDPOINTS from '@/constants/apiEndpoints';
import { CEDEFI_ROUNDS } from '@/constants/cedefiRounds';
import { useContracts } from '@/contexts/ContractsContext';
import { useCheckNetwork } from '@/hooks/useCheckNetwork';
import { useResponsive } from '@/hooks/useResonposive';
import useTransaction from '@/hooks/useTransaction';
import { useCedefiClaimableRewards } from '@/store/useCedefiClaimableRewards';
import useUserInfo from '@/store/useUserInfo';
import { fetcher } from '@/utils/request';
import { useWeb3Modal } from '@web3modal/wagmi/react';
import { notification } from 'antd';
import { ethers } from 'ethers';
import React, { useEffect, useMemo, useState } from 'react';
import { useAccount, useChainId } from 'wagmi';

const UnclaimedRewards: React.FC = () => {
  const { address } = useAccount();
  const { isMobile } = useResponsive();
  const { open } = useWeb3Modal();
  const chainId = useChainId();
  const { isMantaPacific, switchToMantaPacific } = useCheckNetwork();
  const { userInfo } = useUserInfo();
  const { user_address } = userInfo;

  const {
    cedefiRound1Contract,
    cedefiRound2Contract,
    cedefiRound3Contract,
    cedefiRound4Contract,
    cedefiRound5Contract,
    cedefiRound6Contract,
    cedefiRound7Contract,
    cedefiRound8Contract,
    cedefiRound9Contract
  } = useContracts();
  const {
    data: claimableData,
    mutate: refreshClaimableData,
    isLoading,
    error
  } = useCedefiClaimableRewards();

  const [claimStatuses, setClaimStatuses] = useState<{
    [key: number]: boolean;
  }>({});
  const [claimLoading, setClaimLoading] = useState(false);

  const showConnectModal = () => {
    window.isFromConnectWalletToContinueBtn = true;
    open();
  };

  const getDigest = (address: string) =>
    ethers.utils.keccak256(ethers.utils.toUtf8Bytes(address.toLowerCase()));

  const checkClaimStatus = async (contract: any, round: number) => {
    if (!address || !contract) return false;
    const digest = getDigest(address);
    try {
      return await contract.claimStatus(digest);
    } catch (error) {
      console.error(`Check claim status for round ${round} error:`, error);
      return false;
    }
  };

  const checkAllClaimStatuses = async () => {
    if (!address) return;

    const contracts = [
      cedefiRound1Contract,
      cedefiRound2Contract,
      cedefiRound3Contract,
      cedefiRound4Contract,
      cedefiRound5Contract,
      cedefiRound6Contract,
      cedefiRound7Contract,
      cedefiRound8Contract,
      cedefiRound9Contract
    ];

    const promises = contracts.map(async (contract, index) => {
      const round = index + 1;
      if (contract) {
        return { round, status: await checkClaimStatus(contract, round) };
      } else {
        return { round, status: false };
      }
    });

    const results = await Promise.all(promises);
    const statuses: { [key: number]: boolean } = {};
    results.forEach(({ round, status }) => {
      statuses[round] = status;
    });

    setClaimStatuses(statuses);
  };

  const {
    latestUnclaimedRound,
    totalUnclaimed,
    latestUnclaimed,
    latestRoundName
  } = useMemo(() => {
    // 查找最新未claim轮次 限制前9轮 与上方合约数组对应
    const MAX_SUPPORTED_ROUND = CEDEFI_ROUNDS.LATEST_ROUND - 1;

    if (!claimableData || !claimStatuses) {
      console.log('No claimableData or claimStatuses');
      return {
        latestUnclaimedRound: null,
        totalUnclaimed: '- -',
        latestUnclaimed: '- -',
        latestRoundName:
          CEDEFI_ROUNDS.ROUND_NAMES[
            CEDEFI_ROUNDS.LATEST_ROUND as keyof typeof CEDEFI_ROUNDS.ROUND_NAMES
          ]
      };
    }
    let latestRound: number | null = null;
    const roundChecks = Array.from(
      { length: MAX_SUPPORTED_ROUND },
      (_, index) => {
        const round = MAX_SUPPORTED_ROUND - index;
        const isClaimed = claimStatuses[round];
        const roundData = claimableData.find(
          (item: any) => item.round === round
        );
        const hasClaimableReward = roundData?.rewardInfo?.rewardTokenAmount;

        return {
          round,
          isClaimed,
          hasClaimableReward,
          rewardValue: hasClaimableReward ? parseFloat(hasClaimableReward) : 0
        };
      }
    );

    // 找到第一个未claim且有奖励的轮次
    for (const check of roundChecks) {
      if (
        !check.isClaimed &&
        check.hasClaimableReward &&
        check.rewardValue > 0
      ) {
        latestRound = check.round;
        console.log(`Found latest unclaimed round: ${check.round}`);
        break;
      }
    }

    // 计算总未领取数量
    let total = 0;
    const totalCalculations = Array.from(
      { length: MAX_SUPPORTED_ROUND },
      (_, index) => {
        const round = index + 1;
        const isClaimed = claimStatuses[round];
        const roundData = claimableData.find(
          (item: any) => item.round === round
        );
        const claimableReward = roundData?.rewardInfo?.rewardTokenAmount;

        if (!isClaimed && claimableReward && parseFloat(claimableReward) > 0) {
          const rewardValue = parseFloat(claimableReward);
          console.log(`Round ${round} adding to total: ${rewardValue}`);
          return rewardValue;
        }
        return 0;
      }
    );

    total = totalCalculations.reduce((sum, value) => sum + value, 0);

    // 获取最新未claim轮次的数据
    const latestRoundData = latestRound
      ? claimableData.find((item: any) => item.round === latestRound)
      : null;
    const latestUnclaimedAmount =
      latestRoundData?.rewardInfo?.rewardTokenAmount || '- -';

    const result = {
      latestUnclaimedRound: latestRound,
      totalUnclaimed: total > 0 ? total.toFixed(2) : '- -',
      latestUnclaimed: latestUnclaimedAmount,
      latestRoundName: latestRound
        ? CEDEFI_ROUNDS.ROUND_NAMES[
            latestRound as keyof typeof CEDEFI_ROUNDS.ROUND_NAMES
          ]
        : CEDEFI_ROUNDS.ROUND_NAMES[
            CEDEFI_ROUNDS.LATEST_ROUND as keyof typeof CEDEFI_ROUNDS.ROUND_NAMES
          ]
    };

    console.log('Final result:', result);
    return result;
  }, [claimableData, claimStatuses]);

  // 根据轮次获取对应的合约
  const getContractForRound = (round: number) => {
    const contracts = {
      1: cedefiRound1Contract,
      2: cedefiRound2Contract,
      3: cedefiRound3Contract,
      4: cedefiRound4Contract,
      5: cedefiRound5Contract,
      6: cedefiRound6Contract,
      7: cedefiRound7Contract,
      8: cedefiRound8Contract,
      9: cedefiRound9Contract
    };
    return contracts[round as keyof typeof contracts];
  };

  const { run: runClaimToken1, loading: claimTokenLoading1 } = useTransaction(
    cedefiRound1Contract?.claimToken,
    { wait: true }
  );
  const { run: runClaimToken2, loading: claimTokenLoading2 } = useTransaction(
    cedefiRound2Contract?.claimToken,
    { wait: true }
  );
  const { run: runClaimToken3, loading: claimTokenLoading3 } = useTransaction(
    cedefiRound3Contract?.claimToken,
    { wait: true }
  );
  const { run: runClaimToken4, loading: claimTokenLoading4 } = useTransaction(
    cedefiRound4Contract?.claimToken,
    { wait: true }
  );
  const { run: runClaimToken5, loading: claimTokenLoading5 } = useTransaction(
    cedefiRound5Contract?.claimToken,
    { wait: true }
  );
  const { run: runClaimToken6, loading: claimTokenLoading6 } = useTransaction(
    cedefiRound6Contract?.claimToken,
    { wait: true }
  );
  const { run: runClaimToken7, loading: claimTokenLoading7 } = useTransaction(
    cedefiRound7Contract?.claimToken,
    { wait: true }
  );
  const { run: runClaimToken8, loading: claimTokenLoading8 } = useTransaction(
    cedefiRound8Contract?.claimToken,
    { wait: true }
  );
  const { run: runClaimToken9, loading: claimTokenLoading9 } = useTransaction(
    cedefiRound9Contract?.claimToken,
    { wait: true }
  );

  const getClaimTokenFunction = (round: number) => {
    const functions = {
      1: runClaimToken1,
      2: runClaimToken2,
      3: runClaimToken3,
      4: runClaimToken4,
      5: runClaimToken5,
      6: runClaimToken6,
      7: runClaimToken7,
      8: runClaimToken8,
      9: runClaimToken9
    };
    return functions[round as keyof typeof functions];
  };

  const getClaimTokenLoading = (round: number) => {
    const loadings = {
      1: claimTokenLoading1,
      2: claimTokenLoading2,
      3: claimTokenLoading3,
      4: claimTokenLoading4,
      5: claimTokenLoading5,
      6: claimTokenLoading6,
      7: claimTokenLoading7,
      8: claimTokenLoading8,
      9: claimTokenLoading9
    };
    return loadings[round as keyof typeof loadings];
  };

  const handleClaim = async () => {
    if (!latestUnclaimedRound || !address) return;

    setClaimLoading(true);
    try {
      const contract = getContractForRound(latestUnclaimedRound);
      const digest = getDigest(address);
      const status = await contract?.claimStatus(digest);

      if (status) {
        notification.info({
          message: 'Already Claimed'
        });
        setClaimLoading(false);
        return;
      }

      const signRes = await fetcher(
        ENDPOINTS.claimToken,
        {
          method: 'POST',
          body: JSON.stringify({
            round: latestUnclaimedRound
          })
        },
        false
      );

      const {
        receiver,
        tokenAmount,
        sig: { v, r, s }
      } = signRes?.data?.signature || {};

      if (!receiver || !tokenAmount || !v || !r || !s) {
        console.error('Missing signature data:', {
          receiver,
          tokenAmount,
          v,
          r,
          s
        });
        notification.error({
          message: 'Failed to get signature data'
        });
        setClaimLoading(false);
        return;
      }

      await getClaimTokenFunction(latestUnclaimedRound)?.(
        receiver,
        digest,
        tokenAmount,
        v,
        r,
        s
      );

      setTimeout(() => {
        console.log('Refreshing status...');
        checkAllClaimStatuses();
        refreshClaimableData();
      }, 2000);
    } catch (error) {
      console.error('Claim error:', error);
      notification.error({
        message: 'Claim failed',
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setClaimLoading(false);
    }
  };

  useEffect(() => {
    checkAllClaimStatuses();
  }, [
    address,
    cedefiRound1Contract,
    cedefiRound2Contract,
    cedefiRound3Contract,
    cedefiRound4Contract,
    cedefiRound5Contract,
    cedefiRound6Contract,
    cedefiRound7Contract,
    cedefiRound8Contract,
    cedefiRound9Contract
  ]);

  // 如果没有可领取的奖励或未连接钱包，不显示组件
  if (!latestUnclaimedRound || !(user_address && address)) {
    return null;
  }

  return (
    <div className="flex flex-col justify-between items-center p-6 w-full rounded-2xl border bg-white-80 border-primary-green-border">
      <div className="flex w-full items-center justify-between py-4 border-b border-[#05D9A699] border-dashed">
        <div className="flex flex-col justify-between">
          <div className="text-[20px] text-black-title font-[400]">
            Total Unclaimed Manta Rewards
          </div>
        </div>
        <div className="text-[20px] text-black-title font-[300]">
          {totalUnclaimed}
        </div>
      </div>
      <div className="flex justify-between items-center py-4 w-full">
        <div className="flex flex-col justify-between">
          <div className="text-[20px] text-black-title font-[400]">
            {latestRoundName} Pending claim Manta Rewards
          </div>
        </div>
        <div className="text-[40px] text-black-title font-[300]">
          {latestUnclaimed}
        </div>
      </div>
      <div className="w-full">
        <button
          onClick={handleClaim}
          className="btn-primary h-[51px] w-full"
          disabled={
            !latestUnclaimedRound ||
            claimLoading ||
            getClaimTokenLoading(latestUnclaimedRound)
          }
        >
          {claimLoading || getClaimTokenLoading(latestUnclaimedRound) ? (
            <LoadingIcon />
          ) : (
            'Claim'
          )}
        </button>
      </div>
      <div className="mt-2 w-full text-xs italic text-left text-[#1D314CB2]">
        Snapshot Time: July 31, 8PM SGT
      </div>
      <div className="mt-2 w-full text-xs italic text-left text-[#1D314CB2]">
        Claiming Period: July 31, 8PM SGT- August 30, 8PM SGT
      </div>
    </div>
  );
};

export default UnclaimedRewards;
